import ArrowRightOutlined from "@ant-design/icons/ArrowRightOutlined";
import BranchesOutlined from "@ant-design/icons/BranchesOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import StopOutlined from "@ant-design/icons/StopOutlined";
import AddOrEditOrderModal from "features/trades/EditTrade/AddOrEditOrderModal";
import styled, { useTheme } from "styled-components";
import { OrderTypes } from "@shared/features/trades/common/types";
import { EditOrderActionsProps } from "@shared/features/trades/EditTrade/types";
import { openMessage } from "utils/misc";
import { ItemType } from "antd/es/menu/interface";
import Dropdown from "common/Dropdown/Dropdown";
import useEditOrderActions from "@shared/features/trades/EditTrade/useEditOrderActions";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

const EditOrderActions = ({
  orderType,
  orderIndex,
  order,
}: EditOrderActionsProps) => {
  const appTheme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    editOrderModalOpen,
    setEditOrderModalOpen,
    onEditOrderClick,
    onUpdateOrder,
    onCancelClick,
    onCancelAndRedistributeClick,
    onMoveToCurrentPriceClick,
    onMoveToBreakEvenPriceClick,
    invalidEditOrder,
    invalidCancelAndRedistribute,
    moveToCurrentPriceErrors,
    moveToBreakevenPriceErrors,
    invalidBreakevenPriceError,
  } = useEditOrderActions({ orderType, orderIndex, order, openMessage });

  let menuItems: ItemType[] = [
    {
      key: "edit",
      icon: <EditOutlined />,
      label: t("labels.edit"),
      onClick: onEditOrderClick,
      className: invalidEditOrder ? "invalid" : undefined,
    },
    {
      key: "cancel",
      icon: <StopOutlined />,
      label: t("actions.cancel"),
      onClick: onCancelClick,
    },
  ];

  if (orderType !== OrderTypes.STOP) {
    menuItems = menuItems.concat(
      ...[
        {
          key: "cancelAndRedistribute",
          icon: <BranchesOutlined />,
          label: t("labels.cancel_and_redistribute"),
          onClick: onCancelAndRedistributeClick,
          className: invalidCancelAndRedistribute ? "invalid" : undefined,
        },
        {
          key: "move",
          icon: <ArrowRightOutlined />,
          label: t("labels.move_to_current_price"),
          onClick: onMoveToCurrentPriceClick,
          className:
            moveToCurrentPriceErrors.length > 0 ? "invalid" : undefined,
        },
      ]
    );
  } else {
    menuItems.push({
      key: "move",
      icon: <ArrowRightOutlined />,
      label: t("labels.move_to_breakeven_price"),
      onClick: onMoveToBreakEvenPriceClick,
      className:
        moveToBreakevenPriceErrors.length > 0 || invalidBreakevenPriceError
          ? "invalid"
          : undefined,
    });
  }

  const wrapperId = `${orderType}_${orderIndex}_actions`;
  return (
    <Wrapper id={wrapperId}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Dropdown
          title={t("labels.order_actions")}
          menu={{ items: menuItems }}
          overlayStyle={{ userSelect: "none", width: "max-content" }}
          trigger={["click"]}
          getPopupContainer={() => document.getElementById(wrapperId)!}
        >
          <EllipsisOutlined
            style={{
              fontSize: 24,
              cursor: "pointer",
              color: appTheme.colors.basic,
            }}
          />
        </Dropdown>
      </div>
      {editOrderModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <AddOrEditOrderModal
            closeModal={() => setEditOrderModalOpen(false)}
            title={t(ORDER_TYPE_TO_TITLE_DISPLAY[orderType], {
              num: orderIndex + 1,
            })}
            orderType={orderType}
            order={order}
            onOk={onUpdateOrder}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default EditOrderActions;

export const ORDER_TYPE_TO_TITLE_DISPLAY: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  [OrderTypes.ENTRY]: "orders.edit_entry_order",
  [OrderTypes.TAKE_PROFIT]: "orders.edit_take_profit_order",
  [OrderTypes.STOP]: "orders.edit_stop_order",
};

const Wrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;
